import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export interface UptimePoint {
  month_start: string;
  uptime_pct: number;
}

export interface ResolutionPoint {
  month_start: string;
  resolved_count: number;
  avg_resolution_hours: number;
}

export interface RefreshBucket {
  bucket:
    | 'expired'
    | 'expiring_90d'
    | 'expiring_1y'
    | 'healthy'
    | 'no_warranty_data';
  asset_count: number;
  example_solution_types: string[];
}

/**
 * Trend RPCs are per account: the caller passes the account to chart. The
 * RPCs verify the id is one of the caller's links (or the caller is staff).
 */
export function useCustomerUptimeTrend(customerId: string | null, months = 6, locationId?: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customer-uptime-trend', scope.ownerKey, customerId, months, locationId ?? null],
    enabled: scope.ready && !!customerId && scope.ids.includes(customerId),
    queryFn: async (): Promise<UptimePoint[]> => {
      const { data, error } = await supabase.rpc('customer_uptime_trend', {
        p_months: months,
        p_customer_id: customerId!,
        ...(locationId ? { p_location_id: locationId } : {}),
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as UptimePoint[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerResolutionTrend(customerId: string | null, months = 6, locationId?: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customer-resolution-trend', scope.ownerKey, customerId, months, locationId ?? null],
    enabled: scope.ready && !!customerId && scope.ids.includes(customerId),
    queryFn: async (): Promise<ResolutionPoint[]> => {
      const { data, error } = await supabase.rpc('customer_ticket_resolution_trend', {
        p_months: months,
        p_customer_id: customerId!,
        ...(locationId ? { p_location_id: locationId } : {}),
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as ResolutionPoint[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerAssetRefresh(customerId: string | null, locationId?: string) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customer-asset-refresh', scope.ownerKey, customerId, locationId ?? null],
    enabled: scope.ready && !!customerId && scope.ids.includes(customerId),
    queryFn: async (): Promise<RefreshBucket[]> => {
      const { data, error } = await supabase.rpc('customer_asset_refresh_status', {
        p_customer_id: customerId!,
        ...(locationId ? { p_location_id: locationId } : {}),
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as RefreshBucket[];
    },
    staleTime: 30 * 60 * 1000,
  });
}
